import { memo, useEffect, useMemo, useState } from "react";
import {
  compareWithJSON,
  convertWhatsAppFormatToHTML,
  generateCardName,
  getFormattedDate,
  highlightSearchInHTML,
} from "utils/helper";
import { Avatar, Badge } from "antd";
import { Text } from "components/UI";
import { conversationStatuses, randomColors } from "utils/static";
import Tags from "./Tags";
import { ArrowRightOutlined } from "@ant-design/icons";
import DOMPurify from "dompurify";
import { isEmpty, last } from "lodash-es";

const ConversationItem = ({
  item,
  onSelect,
  isSelected,
  index,
  showInfoNew,
  tags,
  assignedAgent,
  search,
  isSearch,
  onCountdownExpired,
}) => {
  const innerMemo = useMemo(() => {
    let messages = item.messages;
    let lastMessage = last(messages) || {};

    // Filter messages based on search keyword if search is provided
    if (isSearch && search && search.trim() !== "") {
      const searchLower = search.toLowerCase();
      messages = item.messages.filter((message) =>
        message.message_text?.toLowerCase().includes(searchLower),
      );
      if (!isEmpty(messages)) {
        lastMessage = last(messages) || {};
      }
    }

    const name = item?.contact?.name || item?.contact_wa_name;
    const cardName = generateCardName(name || "");
    const formattedDate = getFormattedDate(lastMessage.message_time);
    let messageText = convertWhatsAppFormatToHTML(
      lastMessage.message_text || "",
    );
    if (!isEmpty(search)) {
      messageText = highlightSearchInHTML(messageText, search);
    }
    return { lastMessage, cardName, formattedDate, name, messageText };
  }, [
    item?.contact?.name,
    item?.contact_wa_name,
    item.messages,
    search,
    isSearch,
  ]);

  const renderStatus = useMemo(() => {
    if (item.status_id === conversationStatuses.CLOSE) {
      return (
        <div className="px-2 py-0.5 rounded-full bg-red-400 text-white text-[10px] text-center inline-block flex-shrink-0">
          Closed
        </div>
      );
    }
    if (showInfoNew && innerMemo.lastMessage?.is_out === 0) {
      return (
        <div className="px-2 py-0.5 rounded-full bg-green-400 text-white text-[10px] text-center inline-block flex-shrink-0">
          New
        </div>
      );
    }
    return null;
  }, [item.status_id, innerMemo.lastMessage?.is_out, showInfoNew]);

  const cleanText = useMemo(() => {
    return DOMPurify.sanitize(innerMemo.messageText);
  }, [innerMemo.messageText]);

  const [reassignCountdownLabel, setReassignCountdownLabel] = useState(null);
  const conversationId = item.id;
  const showAfter = item.counter?.showAfter;
  const reassignAt = item.counter?.reassignAt;

  useEffect(() => {
    if (showAfter == null || reassignAt == null || !conversationId) {
      setReassignCountdownLabel(null);
      return;
    }
    const getLabel = (now) => {
      if (now < showAfter || now >= reassignAt) return null;
      const sec = Math.max(0, Math.ceil((reassignAt - now) / 1000));
      return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
    };
    // Hitung label langsung saat mount supaya badge tidak kosong 1 detik
    // (berkedip tiap list di-refetch). Expired tetap diproses di tick interval.
    setReassignCountdownLabel(getLabel(Date.now()));
    const interval = setInterval(() => {
      const now = Date.now();
      if (now >= reassignAt) {
        setReassignCountdownLabel(null);
        onCountdownExpired?.(conversationId, reassignAt);
        clearInterval(interval);
        return;
      }
      setReassignCountdownLabel(getLabel(now));
    }, 1000);
    return () => clearInterval(interval);
  }, [conversationId, showAfter, reassignAt, onCountdownExpired]);

  return (
    <div
      onClick={onSelect}
      className={`flex items-start p-3 cursor-pointer transition-all duration-300 ${
        isSelected ? "bg-primary" : "bg-white hover:bg-gray-50"
      }`}
    >
      <div className="flex-shrink-0 mr-3 flex flex-col items-center gap-1">
        <Avatar size={40} style={{ backgroundColor: randomColors[index % 10] }}>
          {innerMemo.cardName}
        </Avatar>
        {reassignCountdownLabel != null && (
          <Badge
            count={reassignCountdownLabel}
            size="small"
            className="flex-shrink-0"
            color={"#faad14"}
            styles={{
              indicator: {
                fontSize: 11,
                fontWeight: 500,
                paddingInline: 5,
                minWidth: 42,
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
                fontFeatureSettings: '"tnum"',
                boxShadow: "none",
              },
            }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-1">
          <Text
            className={`${
              isSelected ? "text-white" : "text-gray-900"
            } font-medium truncate`}
          >
            {`${innerMemo.name} ${
              !isEmpty(item?.contact?.company)
                ? ` - ${item?.contact?.company}`
                : ""
            }`}
          </Text>
          {/* <Text
            className={`ml-2 text-xs whitespace-nowrap ${
              isSelected ? "text-white" : "text-gray-400"
            }`}
          >
            {innerMemo.formattedDate}
          </Text> */}
          <Text
            className={`ml-2 text-xs whitespace-nowrap ${
              isSelected ? "text-white" : "text-gray-400"
            }`}
          >
            {innerMemo.formattedDate}
          </Text>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Text
              className={`text-sm ${
                isSelected ? "text-gray-200" : "text-gray-500"
              } ${isSearch ? "line-clamp-4" : "line-clamp-1"} flex-1`}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanText,
                }}
              />
            </Text>
            {renderStatus}
          </div>
          <Tags tags={tags} />
          {!isEmpty(assignedAgent) && (
            <div
              className={`mt-2 pt-2 border-t ${
                isSelected ? "text-white" : "text-gray-900"
              }`}
            >
              <div className="flex items-center">
                <span
                  className={`text-xs ${
                    isSelected ? "text-gray-200" : "text-gray-400"
                  }`}
                >
                  Assigned to
                </span>
                <div className="flex-1 flex items-center justify-center">
                  <ArrowRightOutlined
                    className={`${
                      isSelected ? "text-gray-200" : "text-gray-400"
                    }`}
                  />
                </div>
                <span className="font-semibold text-sm">{assignedAgent}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(ConversationItem, compareWithJSON);
